import React, { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { Header } from "../components/Header";
import { getIcon } from "../components/ExerciseCard";
import { ChevronLeft, Loader2, Check } from "lucide-react";
import { motion } from "framer-motion";

const API = `/api`;

export default function ExerciseDetailPage() {
  const { exerciseId } = useParams();
  const [exercise, setExercise] = useState(null);
  const [responses, setResponses] = useState({});
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [randomCard, setRandomCard] = useState(null);

  useEffect(() => {
    axios.get(`${API}/exercises/${exerciseId}`).then(r => {
      setExercise(r.data);
      if (r.data.saved_responses) setResponses(r.data.saved_responses);
    });
  }, [exerciseId]);

  const updateResponse = (id, value) => setResponses(prev => ({ ...prev, [id]: value }));

  const toggleChecklist = (id, option) => {
    const current = responses[id] || [];
    const updated = current.includes(option) ? current.filter(o => o !== option) : [...current, option];
    updateResponse(id, updated);
  };

  const drawCard = (options) => {
    const card = options[Math.floor(Math.random() * options.length)];
    setRandomCard(card);
    updateResponse('card', card);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.post(`${API}/exercises/${exerciseId}/respond`, { exercise_id: exerciseId, responses });
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (e) { console.error(e); }
    finally { setSaving(false); }
  };

  if (!exercise) return <div className="min-h-screen bg-[#FAF9F6] flex items-center justify-center"><Loader2 className="w-8 h-8 animate-spin text-[#6B8576]" /></div>;

  const IconComp = getIcon(exercise.icon);

  return (
    <div className="min-h-screen bg-[#FAF9F6]">
      <Header />
      <main className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12">
        <Link to="/exercises" className="text-sm text-[#6A7771] hover:text-[#6B8576] flex items-center gap-1 mb-6 transition-colors duration-200"><ChevronLeft className="w-4 h-4" strokeWidth={1.5} /> Retour aux exercices</Link>

        <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }}>
          <div className="flex items-center gap-4 mb-3">
            <div className="w-14 h-14 rounded-2xl flex items-center justify-center" style={{ backgroundColor: exercise.color + '18' }}>
              <IconComp className="w-7 h-7" style={{ color: exercise.color }} strokeWidth={1.5} />
            </div>
            <div>
              <h1 className="text-2xl font-semibold text-[#2C3531]" style={{ fontFamily: 'Outfit' }}>{exercise.title}</h1>
              {exercise.subtitle && <p className="text-sm text-[#6A7771]">{exercise.subtitle}</p>}
            </div>
          </div>
          <p className="text-[#6A7771] mb-8 pl-[72px] leading-relaxed">{exercise.objective}</p>
        </motion.div>

        <div className="space-y-6">
          {exercise.steps.map((step, si) => (
            <motion.div
              key={si}
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, delay: si * 0.08 }}
              className="bg-white rounded-3xl border border-[#EBE7E0] p-6" data-testid={`step-${si}`}
            >
              <h3 className="font-semibold text-[#2C3531] mb-1.5" style={{ fontFamily: 'Outfit' }}>{step.title}</h3>
              <p className="text-sm text-[#6A7771] mb-5 leading-relaxed">{step.instructions}</p>

              <div className="space-y-5">
                {step.prompts.map(prompt => (
                  <div key={prompt.id}>
                    {prompt.type === 'textarea' && (
                      <div>
                        <label className="block text-sm font-medium text-[#2C3531] mb-1.5">{prompt.label}</label>
                        <textarea
                          className="w-full px-4 py-3.5 rounded-2xl border border-[#EBE7E0] bg-[#FAF9F6] focus:outline-none focus:ring-2 focus:ring-[#6B8576]/20 focus:border-[#6B8576] min-h-[90px] resize-y transition-all duration-300"
                          value={responses[prompt.id] || ''}
                          onChange={e => updateResponse(prompt.id, e.target.value)}
                          placeholder={prompt.placeholder}
                          data-testid={`prompt-${prompt.id}`}
                        />
                      </div>
                    )}
                    {prompt.type === 'choice' && (
                      <div>
                        <label className="block text-sm font-medium text-[#2C3531] mb-2.5">{prompt.label}</label>
                        <div className="flex flex-wrap gap-2">
                          {prompt.options.map(opt => (
                            <button key={opt} type="button" onClick={() => updateResponse(prompt.id, opt)}
                              className={`px-4 py-2 rounded-full text-sm border transition-all duration-300 ${responses[prompt.id] === opt ? 'bg-[#6B8576] text-white border-[#6B8576] shadow-sm' : 'bg-white text-[#2C3531] border-[#EBE7E0] hover:bg-[#FAF9F6] hover:border-[#6B8576]/30'}`}
                              data-testid={`choice-${prompt.id}-${opt}`}
                            >{opt}</button>
                          ))}
                        </div>
                      </div>
                    )}
                    {prompt.type === 'checklist' && (
                      <div>
                        <label className="block text-sm font-medium text-[#2C3531] mb-2.5">{prompt.label}</label>
                        <div className="space-y-2.5">
                          {prompt.options.map(opt => (
                            <label key={opt} className="flex items-center gap-3 cursor-pointer group">
                              <div className={`w-5 h-5 rounded-lg border-2 flex items-center justify-center transition-all duration-300 ${(responses[prompt.id] || []).includes(opt) ? 'bg-[#6B8576] border-[#6B8576]' : 'border-[#EBE7E0] group-hover:border-[#6B8576]/40'}`}>
                                {(responses[prompt.id] || []).includes(opt) && <Check className="w-3 h-3 text-white" strokeWidth={2.5} />}
                              </div>
                              <span className="text-sm text-[#2C3531]">{opt}</span>
                              <input type="checkbox" className="hidden" checked={(responses[prompt.id] || []).includes(opt)} onChange={() => toggleChecklist(prompt.id, opt)} />
                            </label>
                          ))}
                        </div>
                      </div>
                    )}
                    {prompt.type === 'random_card' && (
                      <div>
                        <label className="block text-sm font-medium text-[#2C3531] mb-2.5">{prompt.label}</label>
                        {randomCard && (
                          <motion.div
                            initial={{ opacity: 0, scale: 0.95 }}
                            animate={{ opacity: 1, scale: 1 }}
                            className="bg-[#6B8576]/8 border border-[#6B8576]/15 rounded-2xl p-5 text-center mb-3"
                          >
                            <p className="text-lg font-medium text-[#6B8576] italic leading-relaxed">"{randomCard}"</p>
                          </motion.div>
                        )}
                        <button type="button" onClick={() => drawCard(prompt.options)} className="px-5 py-2.5 bg-[#D9A892] text-white rounded-full text-sm font-medium hover:bg-[#C2937E] transition-all duration-300" data-testid="draw-card-btn">
                          {randomCard ? 'Tirer une autre carte' : 'Tirer une carte'}
                        </button>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </motion.div>
          ))}
        </div>

        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.4 }} className="mt-8">
          <button onClick={handleSave} disabled={saving} className="w-full py-3.5 bg-[#6B8576] text-white rounded-full font-medium hover:bg-[#546A5E] transition-all duration-300 disabled:opacity-50 flex items-center justify-center gap-2" data-testid="save-exercise-btn">
            {saving ? <Loader2 className="w-5 h-5 animate-spin" /> : saved ? <><Check className="w-5 h-5" /> Sauvegarde !</> : 'Sauvegarder mes reponses'}
          </button>
        </motion.div>
      </main>
    </div>
  );
}
